import { ServiceTypeOptions, ZIP_VALIDATION_REGEX } from "@src/constants";

export type GetStartedFormState = Record<string, any>;

export interface GetStartedValidationLabels {
  errorTypeOfService: string;
  errorCareFormat: string;
  errorZip: string;
  errorInvalidZip: string;
  errorAgeRange: string;
  errorPaymentMethod: string;
  errorEnrollmentCategory: string;
  errorInsurancePolicy: string;
  errorSessionType: string;
  errorInsurancePackagesFailed: string;
  errorAddressProcessing: string;
}

export interface GetStartedValidationOptions {
  labels: GetStartedValidationLabels;
  isFeaturePrescriberEnabled: boolean;
  isEnrollmentCategoryPackageFilterEnabled: boolean;
  isSchedulingConsolidationEnabled: boolean;
  requiresServiceTypeSpecialities: ReadonlyArray<string>;
  resolvedZip: string | null;
  addressData: any;
  insurancePolicies: any[];
  policyError: string;
  shouldRequireCareFormat: (
    serviceType: string | undefined,
    specialities?: string[],
    requiredSpecialities?: ReadonlyArray<string>,
  ) => boolean;
}

export const hasUsableAddress = (address: any): boolean => {
  const contextId = address?.context_id;
  return Boolean(contextId && address.state_code);
};

export const isResolvedAddressForZip = (
  zip: string,
  resolvedZip: string | null,
  address: any,
): boolean => Boolean(zip && resolvedZip === zip && hasUsableAddress(address));

const validateZipField = (
  fieldValue: any,
  formState: GetStartedFormState,
  options: GetStartedValidationOptions,
): string | null => {
  if (!fieldValue || !ZIP_VALIDATION_REGEX.test(fieldValue)) {
    return options.labels.errorZip;
  }
  return isResolvedAddressForZip(
    fieldValue,
    options.resolvedZip,
    formState.address || options.addressData,
  )
    ? null
    : options.labels.errorInvalidZip;
};

const validateSelectedPolicyField = (
  fieldValue: any,
  formState: GetStartedFormState,
  options: GetStartedValidationOptions,
): string | null => {
  if (
    formState.zip &&
    !isResolvedAddressForZip(
      formState.zip,
      options.resolvedZip,
      formState.address || options.addressData,
    )
  ) {
    return null;
  }

  const hasNoInsurancePackages = options.insurancePolicies.length === 0;
  const hasNoSelectedPolicy = fieldValue === "" || fieldValue === null;

  if (hasNoInsurancePackages) {
    if (formState.selectPlan === "insurance" || hasNoSelectedPolicy) {
      return options.policyError || options.labels.errorInsurancePackagesFailed;
    }
    return null;
  }

  const isPolicySelectionBlockedByCategory =
    options.isEnrollmentCategoryPackageFilterEnabled &&
    formState.selectPlan === "insurance" &&
    !formState.selectedEnrollmentCategory;

  if (isPolicySelectionBlockedByCategory) {
    return null;
  }

  if (hasNoSelectedPolicy) {
    return options.labels.errorInsurancePolicy;
  }
  return null;
};

const validateCareFormatField = (
  fieldValue: any,
  formState: GetStartedFormState,
  options: GetStartedValidationOptions,
): string | null => {
  const serviceType = options.isFeaturePrescriberEnabled
    ? formState.typeOfService
    : ServiceTypeOptions[0];
  return options.shouldRequireCareFormat(
    serviceType,
    formState.specialities,
    options.requiresServiceTypeSpecialities,
  ) && fieldValue === ""
    ? options.labels.errorCareFormat
    : null;
};

const validateSessionTypeField = (
  fieldValue: any,
  options: GetStartedValidationOptions,
): string | null => {
  const selectedSessionTypes = Array.isArray(fieldValue) ? fieldValue : [];
  return options.isSchedulingConsolidationEnabled && selectedSessionTypes.length === 0
    ? options.labels.errorSessionType
    : null;
};

export const validateGetStartedField = (
  field: string,
  fieldValue: any,
  formState: GetStartedFormState,
  options: GetStartedValidationOptions,
): string | null => {
  switch (field) {
    case "typeOfService":
      return options.isFeaturePrescriberEnabled && fieldValue === ""
        ? options.labels.errorTypeOfService
        : null;
    case "careFormat":
      return validateCareFormatField(fieldValue, formState, options);
    case "sessionType":
      return validateSessionTypeField(fieldValue, options);
    case "zip":
      return validateZipField(fieldValue, formState, options);
    case "selectAge":
      return fieldValue === "" ? options.labels.errorAgeRange : null;
    case "selectPlan":
      return fieldValue === "" ? options.labels.errorPaymentMethod : null;
    case "selectedEnrollmentCategory":
      return options.isEnrollmentCategoryPackageFilterEnabled &&
        formState.selectPlan === "insurance" &&
        !fieldValue
        ? options.labels.errorEnrollmentCategory
        : null;
    case "selectedPolicy":
      return validateSelectedPolicyField(fieldValue, formState, options);
    default:
      return null;
  }
};

export const validateGetStartedForm = (
  value: GetStartedFormState,
  options: GetStartedValidationOptions,
): { errors: Record<string, string>; isValid: boolean } => {
  const errors: Record<string, string> = {
    typeOfService: "",
    careFormat: "",
    sessionType: "",
    zip: "",
    selectAge: "",
    selectPlan: "",
    selectedEnrollmentCategory: "",
    selectedPolicy: "",
  };

  Object.keys(value).forEach((field) => {
    if (field === "selectedPolicy" && value.selectPlan === "self_pay") {
      return;
    }

    const error = validateGetStartedField(field, value[field], value, options);
    if (error) {
      errors[field] = error;
    }
  });

  return { errors, isValid: Object.values(errors).every((error) => error === "") };
};
